import type { JSX } from "react";

import type { ProviderEvidence, SourceType } from "../types/visibility";
import { LLM_COLORS } from "../utils/llmColors";
import { humanize } from "../utils/sourceTypes";

interface VisibilitySourceMixProps {
  sourceTypeCounts: Partial<Record<SourceType, number>>;
  providers: ProviderEvidence[];
}

/**
 * "What the engines read": a most-common-first count list of source types, followed by a
 * per-provider row showing whether that engine searched the web at all.
 */
const VisibilitySourceMix = ({
  sourceTypeCounts,
  providers,
}: VisibilitySourceMixProps): JSX.Element => {
  const entries = Object.entries(sourceTypeCounts) as [SourceType, number][];
  const maxCount = Math.max(1, ...entries.map(([, count]) => count));

  return (
    <div className="space-y-4">
      {entries.length > 0 && (
        <div className="space-y-2">
          {entries.map(([sourceType, count]) => (
            <div key={sourceType} className="flex items-center gap-3">
              <span className="text-sm text-on-surface-variant w-36 shrink-0 truncate">
                {humanize(sourceType)}
              </span>
              <div className="flex-1 h-2 bg-surface-container-low rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${(count / maxCount) * 100}%` }}
                />
              </div>
              <span className="text-sm font-semibold text-on-surface w-6 text-right shrink-0">
                {count}
              </span>
            </div>
          ))}
        </div>
      )}

      {providers.length > 0 && (
        <div className="space-y-2">
          {providers.map((provider) => {
            const style = LLM_COLORS[provider.llm_provider] || {
              bg: "bg-surface-container-low",
              text: "text-on-surface",
              border: "border-surface-container-high",
              label: provider.llm_provider,
            };
            return (
              <div
                key={provider.llm_provider}
                className="flex items-center justify-between gap-3 p-3 rounded-xl border border-surface-container-high bg-surface-container-lowest"
              >
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full border shrink-0 ${style.bg} ${style.text} ${style.border}`}
                >
                  {style.label}
                </span>
                {provider.searched_web ? (
                  <div className="flex flex-wrap gap-x-3 gap-y-1 justify-end">
                    <span className="text-sm text-on-surface-variant">
                      Cited {provider.cited_source_count} source
                      {provider.cited_source_count === 1 ? "" : "s"}
                    </span>
                    {provider.sources.map((source) => (
                      <a
                        key={source.url}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={source.url}
                        className="text-sm text-primary hover:underline"
                      >
                        {source.domain}
                      </a>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-on-surface-variant italic">
                    Answered from memory — cited no sources
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VisibilitySourceMix;
